<template>
  <div class="einvoice-status-wrapper">
    <!-- Compact Status Pill -->
    <button
      :id="popoverId"
      type="button"
      :class="['einvoice-pill', pillConfig.class, sizeClass]"
      :aria-label="pillConfig.text"
      @click.stop="$emit('click')"
      v-if="workState !== 'none'"
    >
      <i v-if="pillConfig.icon" :class="pillConfig.icon"></i>
      <span>{{ pillConfig.text }}</span>
      <span v-if="hasWarning" class="warning-dot" aria-hidden="true"></span>
    </button>

    <!-- Popover with Details -->
    <b-popover
      v-if="showPopover"
      :target="popoverId"
      triggers="hover focus"
      placement="left"
      custom-class="einvoice-status-popover"
    >
      <template #title>Thông tin tra cứu</template>

      <div class="popover-detail">
        <div
          v-for="item in referenceItems"
          :key="item.label"
          class="detail-row"
        >
          <span class="detail-label">{{ item.label }}</span>
          <span :class="['detail-value', item.className]">{{ item.value }}</span>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
  const PILL_CONFIGS = {
    none: { class: 'pill-gray', icon: null, text: 'Chưa tạo HĐĐT' },
    draft: { class: 'pill-blue', icon: 'fas fa-file-alt', text: 'Nháp' },
    pending: { class: 'pill-yellow', icon: 'fas fa-sync-alt fa-spin', text: 'Đang xử lý' },
    issued: { class: 'pill-green', icon: 'fas fa-check', text: 'Đã phát hành' },
    cancelled: { class: 'pill-dark', icon: 'fas fa-ban', text: 'Đã hủy' },
    adjusted: { class: 'pill-orange', icon: 'fas fa-pen', text: 'Đã điều chỉnh' },
    replaced: { class: 'pill-purple', icon: 'fas fa-exchange-alt', text: 'Đã thay thế' },
    failed: { class: 'pill-red', icon: 'fas fa-exclamation-triangle', text: 'Lỗi phát hành' },
  }

  export default {
    name: 'EInvoiceStatusBadge',

    props: {
      workState: {
        type: String,
        default: 'none',
        validator: (v) => Object.keys(PILL_CONFIGS).includes(v),
      },
      invoiceNo: { type: String, default: null },
      pattern: { type: String, default: null },
      serial: { type: String, default: null },
      issuedAt: { type: String, default: null },
      errorMessage: { type: String, default: null },
      taxStatus: { type: String, default: null },
      size: {
        type: String,
        default: 'md',
        validator: (v) => ['sm', 'md'].includes(v),
      },
    },

    emits: ['click'],

    data() {
      return {
        popoverId: `einvoice-pill-${Math.random().toString(36).slice(2, 9)}`,
      }
    },

    computed: {
      pillConfig() {
        return PILL_CONFIGS[this.workState] || PILL_CONFIGS.none
      },
      showPopover() {
        return this.workState === 'issued' && this.referenceItems.length > 0
      },
      referenceItems() {
        const items = []

        if (this.invoiceNo) {
          items.push({
            label: 'Số hóa đơn',
            value: this.invoiceNo,
            className: 'font-weight-bold',
          })
        }

        if (this.pattern || this.serial) {
          items.push({
            label: 'Mẫu / Ký hiệu',
            value: [this.pattern, this.serial].filter(Boolean).join(' / '),
            className: '',
          })
        }

        if (this.issuedAt) {
          items.push({
            label: 'Ngày phát hành',
            value: this.formatDate(this.issuedAt),
            className: '',
          })
        }

        return items
      },
      sizeClass() {
        return this.size === 'sm' ? 'einvoice-pill--sm' : ''
      },
      hasWarning() {
        return this.workState === 'failed' || this.taxStatus === 'rejected'
      },
    },

    methods: {
      formatDate(isoDate) {
        if (!isoDate) return ''
        return new Date(isoDate).toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .einvoice-status-wrapper {
    display: inline-block;
  }

  .einvoice-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border: none;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.15s ease, transform 0.15s ease;
    position: relative;
    white-space: nowrap;
    line-height: 1.4;

    &:hover {
      filter: brightness(0.93);
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 2px solid rgba(59, 130, 246, 0.6);
      outline-offset: 2px;
    }

    i {
      font-size: 10px;
      flex-shrink: 0;
    }
  }

  .einvoice-pill--sm {
    font-size: 10px;
    padding: 3px 8px;
    gap: 4px;

    i { font-size: 9px; }
  }

  /* Semantic color tokens */
  .pill-gray   { background: #f1f3f5; color: #868e96; }
  .pill-blue   { background: #e7f5ff; color: #1c7ed6; }
  .pill-yellow { background: #fff9db; color: #e67700; }
  .pill-green  { background: #d3f9d8; color: #2f9e44; }
  .pill-red    { background: #ffe3e3; color: #c92a2a; }
  .pill-orange { background: #fff4e6; color: #d9480f; }
  .pill-purple { background: #f3d9fa; color: #862e9c; }
  .pill-dark   { background: #e9ecef; color: #495057; }

  .warning-dot {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 8px;
    height: 8px;
    background: #c92a2a;
    border-radius: 50%;
    border: 2px solid #fff;
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }

  /* Popover overrides */
  :deep(.einvoice-status-popover) {
    min-width: 230px;
    max-width: 300px;

    .popover-header {
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
      padding: 8px 14px;
    }

    .popover-body {
      padding: 6px 14px 10px;
    }
  }

  .popover-header-content {
    font-size: 13px;
    font-weight: 600;
    color: #343a40;
  }

  .popover-detail {
    display: flex;
    flex-direction: column;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 6px 0;
    font-size: 12px;
    border-bottom: 1px solid #f1f3f5;

    &:last-child { border-bottom: none; }
  }

  .detail-label {
    color: #868e96;
    flex-shrink: 0;
    font-size: 11px;
  }

  .detail-value {
    text-align: right;
    word-break: break-word;
    font-size: 12px;
  }
</style>
